import type { PlayerId } from '../../shared/types/ids';
import type {
  BodyZone,
  ThriveInputEvent,
  ThriveInputEventOf,
  ThriveInputEventType,
} from '../../shared/types/input-events';
import type { PlayerInputSnapshot, SemanticActionKind, SemanticInputReader } from '../../sdk/input';
import type { SemanticEventBus, Unsubscribe } from './semantic-event-bus';

/**
 * Read-only view of the semantic event bus, and the only input surface a game scene ever
 * gets (module spec §9). It derives state from events alone: no frames, no landmarks, no
 * `getUserMedia`. Keyboard and camera adapters feed it identically.
 */

const ACTION_BY_EVENT: Readonly<Record<ThriveInputEventType, SemanticActionKind | null>> = {
  PLAYER_ENTERED_FRAME: null,
  PLAYER_LEFT_FRAME: null,
  PLAYER_ZONE_CHANGED: 'ENTER_ZONE',
  PLAYER_JUMPED: 'JUMP',
  PLAYER_CROUCHED: 'CROUCH',
  PLAYER_STOOD: 'STAND',
  PLAYER_RAISED_HANDS: 'RAISE_HANDS',
  PLAYER_LOWERED_HANDS: 'LOWER_HANDS',
  PLAYER_BECAME_STILL: 'BE_STILL',
  PLAYER_MOVED: 'MOVE',
};

/** Bounded so a long session cannot grow this without limit. */
const MAX_HISTORY_PER_PLAYER = 200;

interface ActionRecord {
  kind: SemanticActionKind;
  at: number;
}

export interface DisposableSemanticInputReader extends SemanticInputReader {
  /** Detaches from the bus. Call on session teardown. */
  dispose(): void;
}

function emptySnapshot(playerId: PlayerId): PlayerInputSnapshot {
  return {
    playerId,
    present: false,
    zone: null,
    handsRaised: false,
    crouched: false,
    still: false,
    lastEventAt: null,
  };
}

export function createSemanticInputReader(bus: SemanticEventBus): DisposableSemanticInputReader {
  const snapshots = new Map<PlayerId, PlayerInputSnapshot>();
  const history = new Map<PlayerId, ActionRecord[]>();

  const snapshotFor = (playerId: PlayerId): PlayerInputSnapshot => {
    const existing = snapshots.get(playerId);
    if (existing !== undefined) return existing;
    const created = emptySnapshot(playerId);
    snapshots.set(playerId, created);
    return created;
  };

  const apply = (event: ThriveInputEvent): void => {
    const current = snapshotFor(event.playerId);
    const next: PlayerInputSnapshot = { ...current, lastEventAt: event.at };

    switch (event.type) {
      case 'PLAYER_ENTERED_FRAME':
        next.present = true;
        break;
      case 'PLAYER_LEFT_FRAME':
        next.present = false;
        break;
      case 'PLAYER_ZONE_CHANGED':
        next.zone = event.zone satisfies BodyZone;
        break;
      case 'PLAYER_CROUCHED':
        next.crouched = true;
        next.still = false;
        break;
      case 'PLAYER_STOOD':
        next.crouched = false;
        break;
      case 'PLAYER_RAISED_HANDS':
        next.handsRaised = true;
        break;
      case 'PLAYER_LOWERED_HANDS':
        next.handsRaised = false;
        break;
      case 'PLAYER_BECAME_STILL':
        next.still = true;
        break;
      case 'PLAYER_MOVED':
      case 'PLAYER_JUMPED':
        next.still = false;
        break;
    }

    snapshots.set(event.playerId, next);

    const kind = ACTION_BY_EVENT[event.type];
    if (kind !== null) {
      const records = history.get(event.playerId) ?? [];
      records.push({ kind, at: event.at });
      if (records.length > MAX_HISTORY_PER_PLAYER)
        records.splice(0, records.length - MAX_HISTORY_PER_PLAYER);
      history.set(event.playerId, records);
    }
  };

  const unsubscribeState: Unsubscribe = bus.subscribeAll(apply);

  return {
    subscribe(listener: (event: ThriveInputEvent) => void): Unsubscribe {
      return bus.subscribeAll(listener);
    },

    subscribeTo<TType extends ThriveInputEventType>(
      type: TType,
      listener: (event: ThriveInputEventOf<TType>) => void,
    ): Unsubscribe {
      return bus.subscribe(type, listener);
    },

    getSnapshot(playerId: PlayerId): PlayerInputSnapshot | null {
      return snapshots.get(playerId) ?? null;
    },

    /**
     * Who the reader has seen — never who it saw first (R2-8, COOP-07, D-052(7)).
     *
     * This is on the SDK surface a game scene is handed, so left in Map insertion order it hands
     * every scene a record of which child acted first: exactly the head-to-head the
     * non-competitive invariant exists to make unrepresentable.
     *
     * Sorted by id rather than routed through `inRosterOrder`, because the reader genuinely has
     * no roster: it is fed by input adapters, not by a session, and can be constructed before
     * anybody has been seated. Sorting is the same rule that helper already applies to an id the
     * roster does not know, and the platform's lane ids (`hero-1`, `hero-2`, fixed for a whole
     * mission — MVP notes §6, COOP-08) sort into lane order. A roster passed in here would be a
     * second source of truth for seating that the reader has no way to keep current.
     */
    listPlayerIds(): PlayerId[] {
      return [...snapshots.keys()].sort();
    },

    countSince(playerId: PlayerId, kind: SemanticActionKind, sinceMs: number): number {
      const records = history.get(playerId);
      if (records === undefined) return 0;
      return records.filter((record) => record.kind === kind && record.at >= sinceMs).length;
    },

    dispose(): void {
      unsubscribeState();
      snapshots.clear();
      history.clear();
    },
  };
}
